import { useState, useRef, useEffect } from 'react';
import { API, toFullUrl } from '../config/api';
import axios from 'axios';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import {
  Key, Upload, Music, Loader2, CheckCircle, ArrowLeft, UserPlus,
  Clock, X, FolderOpen, Film, Image
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

export default function ArtistPortal() {
  const [mode, setMode] = useState('home'); // home, code, upload, request
  const [accessCode, setAccessCode] = useState('');
  const [artist, setArtist] = useState(null);
  const [verifying, setVerifying] = useState(false);

  // Upload state
  const [title, setTitle] = useState('');
  const [audioFile, setAudioFile] = useState(null);
  const [coverFile, setCoverFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploaded, setUploaded] = useState(false);
  const audioInputRef = useRef(null);
  const coverInputRef = useRef(null);

  // Free media state
  const [freeTitle, setFreeTitle] = useState('');
  const [freeFile, setFreeFile] = useState(null);
  const [freeThumb, setFreeThumb] = useState(null);
  const [freeType, setFreeType] = useState('audio');
  const [uploadingFree, setUploadingFree] = useState(false);
  const [uploadedFree, setUploadedFree] = useState(false);
  const freeFileRef = useRef(null);
  const freeThumbRef = useRef(null);

  // Request state
  const [reqForm, setReqForm] = useState({ name: '', email: '', bio: '', links: {} });
  const [requesting, setRequesting] = useState(false);
  const [requested, setRequested] = useState(false);

  const verifyCode = async () => {
    if (!accessCode.trim()) return;
    setVerifying(true);
    try {
      const { data } = await axios.post(`${API}/featured-artists/portal/verify`, { access_code: accessCode });
      setArtist(data);
      setMode('upload');
      toast.success(`Welcome, ${data.name}!`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid access code');
    } finally {
      setVerifying(false);
    }
  };

  const uploadTrack = async () => {
    if (!title.trim() || !audioFile) return toast.error('Title and audio file are required');
    setUploading(true);
    const fd = new FormData();
    fd.append('access_code', accessCode);
    fd.append('title', title);
    fd.append('audio', audioFile);
    if (coverFile) fd.append('cover', coverFile);
    try {
      await axios.post(`${API}/featured-artists/portal/upload-track`, fd);
      setUploaded(true);
      setTitle('');
      setAudioFile(null);
      setCoverFile(null);
      toast.success('Track uploaded! Awaiting admin approval.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const submitRequest = async () => {
    if (!reqForm.name.trim() || !reqForm.email.trim()) return toast.error('Name and email required');
    setRequesting(true);
    try {
      await axios.post(`${API}/featured-artists/request`, reqForm);
      setRequested(true);
      toast.success('Request submitted!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Request failed');
    } finally {
      setRequesting(false);
    }
  };

  const uploadFreeMedia = async () => {
    if (!freeTitle.trim() || !freeFile) return toast.error('Title and file are required');
    setUploadingFree(true);
    try {
      const formData = new FormData();
      formData.append('access_code', accessCode);
      formData.append('title', freeTitle.trim());
      formData.append('media_type', freeType);
      formData.append('file', freeFile);
      if (freeThumb) formData.append('thumbnail', freeThumb);
      await axios.post(`${API}/featured-artists/portal/upload-free-media`, formData);
      setUploadedFree(true);
      setFreeTitle(''); setFreeFile(null); setFreeThumb(null);
      toast.success('Free media uploaded! Pending admin approval.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploadingFree(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="artist-portal">
      <Helmet><title>Featured Artist Portal</title></Helmet>

      <div className="max-w-lg mx-auto px-4 py-16 sm:py-24">
        <Link to="/" className="text-sm text-[#A3A3A3] hover:text-[#FF3B30] transition-colors flex items-center gap-2 mb-8">
          <ArrowLeft className="w-4 h-4" /> Back to Home
        </Link>

        <p className="overline text-[#FF3B30] mb-2">FEATURED ARTIST</p>
        <h1 className="font-cabinet text-3xl sm:text-4xl font-black uppercase tracking-tighter mb-8">
          ARTIST PORTAL
        </h1>

        {/* HOME — Choose action */}
        {mode === 'home' && (
          <div className="space-y-4" data-testid="portal-home">
            <button onClick={() => setMode('code')}
              className="w-full flex items-center gap-4 p-5 bg-[#141414] border border-[#262626] hover:border-[#DAA520]/30 transition-colors text-left"
              data-testid="enter-code-btn">
              <div className="w-12 h-12 bg-[#DAA520]/10 border border-[#DAA520]/30 flex items-center justify-center flex-shrink-0">
                <Key className="w-6 h-6 text-[#DAA520]" />
              </div>
              <div>
                <p className="font-bold">I Have an Access Code</p>
                <p className="text-xs text-[#A3A3A3]">Enter your code to upload music</p>
              </div>
            </button>
            <button onClick={() => setMode('request')}
              className="w-full flex items-center gap-4 p-5 bg-[#141414] border border-[#262626] hover:border-[#DAA520]/30 transition-colors text-left"
              data-testid="request-feature-btn">
              <div className="w-12 h-12 bg-[#FF3B30]/10 border border-[#FF3B30]/30 flex items-center justify-center flex-shrink-0">
                <UserPlus className="w-6 h-6 text-[#FF3B30]" />
              </div>
              <div>
                <p className="font-bold">Request to Be Featured</p>
                <p className="text-xs text-[#A3A3A3]">Submit your profile for review</p>
              </div>
            </button>
          </div>
        )}

        {/* CODE ENTRY */}
        {mode === 'code' && !artist && (
          <div className="space-y-4 bg-[#141414] border border-[#262626] p-6" data-testid="code-entry">
            <div>
              <Label className="text-xs text-[#DAA520] font-bold uppercase">Access Code</Label>
              <Input value={accessCode} onChange={e => setAccessCode(e.target.value)}
                placeholder="Paste your access code..."
                className="mt-2 bg-[#0A0A0A] border-[#262626] rounded-none text-lg font-mono tracking-wider"
                onKeyDown={e => e.key === 'Enter' && verifyCode()}
                data-testid="access-code-input" />
            </div>
            <div className="flex gap-2">
              <Button onClick={verifyCode} disabled={verifying || !accessCode.trim()}
                className="flex-1 rounded-none bg-[#DAA520] text-black font-bold" data-testid="verify-code-btn">
                {verifying ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Key className="w-4 h-4 mr-2" />}
                VERIFY CODE
              </Button>
              <Button variant="outline" onClick={() => setMode('home')}
                className="rounded-none border-[#262626] text-[#A3A3A3]">
                BACK
              </Button>
            </div>
          </div>
        )}

        {/* UPLOAD PORTAL */}
        {mode === 'upload' && artist && (
          <div className="space-y-6" data-testid="upload-portal">
            {/* Artist info */}
            <div className="flex items-center gap-4 bg-[#141414] border border-[#262626] p-4">
              {artist.photo ? (
                <img src={toFullUrl(artist.photo)} alt="" className="w-14 h-14 object-cover" />
              ) : (
                <div className="w-14 h-14 bg-[#262626] flex items-center justify-center">
                  <Music className="w-6 h-6 text-[#666]" />
                </div>
              )}
              <div>
                <p className="font-bold text-lg">{artist.name}</p>
                <p className="text-xs text-green-400 flex items-center gap-1">
                  <CheckCircle className="w-3 h-3" /> Verified Artist
                </p>
              </div>
            </div>

            <PayoutSection accessCode={accessCode} />

            {uploaded ? (
              <div className="bg-green-400/10 border border-green-400/30 p-6 text-center space-y-3" data-testid="upload-success">
                <CheckCircle className="w-10 h-10 text-green-400 mx-auto" />
                <h3 className="font-bold text-lg">Track Uploaded!</h3>
                <p className="text-sm text-[#A3A3A3]">Your track is pending admin approval. You'll be featured once approved.</p>
                <Button onClick={() => setUploaded(false)} className="rounded-none bg-[#DAA520] text-black font-bold" data-testid="upload-another-btn">
                  UPLOAD ANOTHER
                </Button>
              </div>
            ) : (
              <div className="bg-[#141414] border border-[#262626] p-6 space-y-4" data-testid="upload-form">
                <h3 className="font-cabinet font-bold uppercase">Upload a Track</h3>
                <p className="text-xs text-[#A3A3A3]">Your track will be reviewed by the admin before being published.</p>
                <div>
                  <Label className="text-xs text-[#DAA520] font-bold uppercase">Track Title *</Label>
                  <Input value={title} onChange={e => setTitle(e.target.value)}
                    placeholder="Song name" className="mt-1 bg-[#0A0A0A] border-[#262626] rounded-none" data-testid="track-title-input" />
                </div>
                <div>
                  <Label className="text-xs text-[#DAA520] font-bold uppercase">Audio File *</Label>
                  <button onClick={() => audioInputRef.current?.click()}
                    className="mt-1 w-full p-4 border border-dashed border-[#333] bg-[#0A0A0A] hover:border-[#DAA520]/30 transition-colors text-left"
                    data-testid="audio-upload-area">
                    {audioFile ? (
                      <div className="flex items-center gap-2">
                        <Music className="w-5 h-5 text-[#DAA520]" />
                        <span className="text-sm truncate">{audioFile.name}</span>
                        <button onClick={(e) => { e.stopPropagation(); setAudioFile(null); }} className="ml-auto text-[#666] hover:text-red-500">
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 text-[#666]">
                        <Upload className="w-5 h-5" />
                        <span className="text-sm">Click to select audio file (.mp3, .wav, .flac)</span>
                      </div>
                    )}
                  </button>
                  <input ref={audioInputRef} type="file" accept="audio/*" className="hidden"
                    onChange={e => setAudioFile(e.target.files?.[0] || null)} />
                </div>
                <div>
                  <Label className="text-xs text-[#DAA520] font-bold uppercase">Cover Art (optional)</Label>
                  <button onClick={() => coverInputRef.current?.click()}
                    className="mt-1 w-full p-4 border border-dashed border-[#333] bg-[#0A0A0A] hover:border-[#DAA520]/30 transition-colors text-left"
                    data-testid="cover-upload-area">
                    {coverFile ? (
                      <div className="flex items-center gap-2">
                        <img src={URL.createObjectURL(coverFile)} alt="" className="w-10 h-10 object-cover" />
                        <span className="text-sm truncate">{coverFile.name}</span>
                        <button onClick={(e) => { e.stopPropagation(); setCoverFile(null); }} className="ml-auto text-[#666] hover:text-red-500">
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 text-[#666]">
                        <Upload className="w-5 h-5" />
                        <span className="text-sm">Click to select cover image</span>
                      </div>
                    )}
                  </button>
                  <input ref={coverInputRef} type="file" accept="image/*" className="hidden"
                    onChange={e => setCoverFile(e.target.files?.[0] || null)} />
                </div>
                <Button onClick={uploadTrack} disabled={uploading || !title.trim() || !audioFile}
                  className="w-full rounded-none bg-[#DAA520] text-black font-bold h-12" data-testid="submit-track-btn">
                  {uploading ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> UPLOADING...</> : <><Upload className="w-5 h-5 mr-2" /> SUBMIT TRACK FOR REVIEW</>}
                </Button>
              </div>
            )}

            {/* Free Media Upload */}
            <div className="bg-[#141414] border border-[#262626] p-6 space-y-4" data-testid="free-media-upload">
              <h3 className="font-cabinet font-bold uppercase flex items-center gap-2">
                <FolderOpen className="w-5 h-5 text-[#DAA520]" />
                Upload Free Media
              </h3>
              <p className="text-xs text-[#A3A3A3]">Share free content with your fans — audio, videos, or images.</p>

              {uploadedFree ? (
                <div className="bg-green-400/10 border border-green-400/30 p-4 text-center space-y-2">
                  <CheckCircle className="w-8 h-8 text-green-400 mx-auto" />
                  <p className="font-bold">Free Media Uploaded!</p>
                  <p className="text-xs text-[#A3A3A3]">Pending admin approval.</p>
                  <Button onClick={() => setUploadedFree(false)} size="sm" className="rounded-none bg-[#DAA520] text-black font-bold">
                    UPLOAD MORE
                  </Button>
                </div>
              ) : (
                <>
                  <div>
                    <Label className="text-xs text-[#DAA520] font-bold uppercase">Media Type</Label>
                    <div className="flex gap-2 mt-1.5">
                      {[
                        { id: 'audio', icon: Music, label: 'Audio' },
                        { id: 'video', icon: Film, label: 'Video' },
                        { id: 'image', icon: Image, label: 'Image' },
                      ].map(t => (
                        <button key={t.id} onClick={() => setFreeType(t.id)}
                          className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-none border text-xs font-bold uppercase transition-colors ${
                            freeType === t.id ? 'border-[#DAA520] bg-[#DAA520]/10 text-[#DAA520]' : 'border-[#262626] text-[#737373] hover:border-[#404040]'
                          }`} data-testid={`free-type-${t.id}`}>
                          <t.icon className="w-3.5 h-3.5" /> {t.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs text-[#DAA520] font-bold uppercase">Title *</Label>
                    <Input value={freeTitle} onChange={e => setFreeTitle(e.target.value)}
                      placeholder="Media title" className="mt-1 bg-[#0A0A0A] border-[#262626] rounded-none" data-testid="free-title-input" />
                  </div>
                  <div>
                    <Label className="text-xs text-[#DAA520] font-bold uppercase">File *</Label>
                    <button onClick={() => freeFileRef.current?.click()}
                      className="mt-1 w-full p-4 border border-dashed border-[#333] bg-[#0A0A0A] hover:border-[#DAA520]/30 transition-colors text-left"
                      data-testid="free-file-area">
                      {freeFile ? (
                        <div className="flex items-center gap-2">
                          <CheckCircle className="w-4 h-4 text-green-400" />
                          <span className="text-sm truncate">{freeFile.name}</span>
                          <button onClick={(e) => { e.stopPropagation(); setFreeFile(null); }} className="ml-auto text-[#666] hover:text-red-500">
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 text-[#666]">
                          <Upload className="w-5 h-5" />
                          <span className="text-sm">Select {freeType} file</span>
                        </div>
                      )}
                    </button>
                    <input ref={freeFileRef} type="file"
                      accept={freeType === 'audio' ? 'audio/*' : freeType === 'video' ? 'video/*' : 'image/*'}
                      className="hidden" onChange={e => setFreeFile(e.target.files?.[0] || null)} />
                  </div>
                  <div>
                    <Label className="text-xs text-[#DAA520] font-bold uppercase">Thumbnail (optional)</Label>
                    <button onClick={() => freeThumbRef.current?.click()}
                      className="mt-1 w-full p-3 border border-dashed border-[#333] bg-[#0A0A0A] hover:border-[#DAA520]/30 transition-colors text-left"
                      data-testid="free-thumb-area">
                      {freeThumb ? (
                        <div className="flex items-center gap-2">
                          <img src={URL.createObjectURL(freeThumb)} alt="" className="w-8 h-8 object-cover" />
                          <span className="text-sm truncate">{freeThumb.name}</span>
                          <button onClick={(e) => { e.stopPropagation(); setFreeThumb(null); }} className="ml-auto text-[#666] hover:text-red-500">
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 text-[#666] text-sm">
                          <Upload className="w-4 h-4" /> Thumbnail image
                        </div>
                      )}
                    </button>
                    <input ref={freeThumbRef} type="file" accept="image/*" className="hidden"
                      onChange={e => setFreeThumb(e.target.files?.[0] || null)} />
                  </div>
                  <Button onClick={uploadFreeMedia} disabled={uploadingFree || !freeTitle.trim() || !freeFile}
                    className="w-full rounded-none bg-[#22C55E] hover:bg-[#16A34A] text-black font-bold h-11" data-testid="submit-free-btn">
                    {uploadingFree ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> UPLOADING...</> : <><FolderOpen className="w-4 h-4 mr-2" /> UPLOAD FREE MEDIA</>}
                  </Button>
                </>
              )}
            </div>
          </div>
        )}

        {/* REQUEST FORM */}
        {mode === 'request' && (
          <div className="space-y-4" data-testid="request-form">
            {requested ? (
              <div className="bg-green-400/10 border border-green-400/30 p-6 text-center space-y-3" data-testid="request-success">
                <CheckCircle className="w-10 h-10 text-green-400 mx-auto" />
                <h3 className="font-bold text-lg">Request Submitted!</h3>
                <p className="text-sm text-[#A3A3A3]">The admin will review your request. If approved, you'll receive an access code.</p>
                <Link to="/">
                  <Button variant="outline" className="rounded-none border-[#262626] text-[#A3A3A3]">
                    <ArrowLeft className="w-4 h-4 mr-2" /> BACK TO HOME
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="bg-[#141414] border border-[#262626] p-6 space-y-4">
                <h3 className="font-cabinet font-bold uppercase">Request to Be Featured</h3>
                <p className="text-xs text-[#A3A3A3]">Fill out your info below. The admin will review and may grant you access to upload your music.</p>
                <div>
                  <Label className="text-xs text-[#DAA520] font-bold uppercase">Name *</Label>
                  <Input value={reqForm.name} onChange={e => setReqForm(p => ({ ...p, name: e.target.value }))}
                    placeholder="Your artist name" className="mt-1 bg-[#0A0A0A] border-[#262626] rounded-none" data-testid="request-name" />
                </div>
                <div>
                  <Label className="text-xs text-[#DAA520] font-bold uppercase">Email *</Label>
                  <Input value={reqForm.email} onChange={e => setReqForm(p => ({ ...p, email: e.target.value }))}
                    placeholder="your@email.com" className="mt-1 bg-[#0A0A0A] border-[#262626] rounded-none" data-testid="request-email" />
                </div>
                <div>
                  <Label className="text-xs text-[#DAA520] font-bold uppercase">Bio</Label>
                  <Textarea value={reqForm.bio} onChange={e => setReqForm(p => ({ ...p, bio: e.target.value }))}
                    placeholder="Tell us about yourself..." rows={3} className="mt-1 bg-[#0A0A0A] border-[#262626] rounded-none" data-testid="request-bio" />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-[10px] text-[#666] uppercase">Spotify</Label>
                    <Input value={reqForm.links.spotify || ''} onChange={e => setReqForm(p => ({ ...p, links: { ...p.links, spotify: e.target.value } }))}
                      placeholder="URL" className="bg-[#0A0A0A] border-[#262626] rounded-none text-xs h-8" />
                  </div>
                  <div>
                    <Label className="text-[10px] text-[#666] uppercase">Instagram</Label>
                    <Input value={reqForm.links.instagram || ''} onChange={e => setReqForm(p => ({ ...p, links: { ...p.links, instagram: e.target.value } }))}
                      placeholder="URL" className="bg-[#0A0A0A] border-[#262626] rounded-none text-xs h-8" />
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button onClick={submitRequest} disabled={requesting || !reqForm.name.trim() || !reqForm.email.trim()}
                    className="flex-1 rounded-none bg-[#DAA520] text-black font-bold" data-testid="submit-request-btn">
                    {requesting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <UserPlus className="w-4 h-4 mr-2" />}
                    SUBMIT REQUEST
                  </Button>
                  <Button variant="outline" onClick={() => setMode('home')}
                    className="rounded-none border-[#262626] text-[#A3A3A3]">
                    BACK
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function PayoutSection({ accessCode }) {
  const [wallet, setWallet] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    payout_method: 'paypal', payout_handle: '', payout_name: '',
    cadence: 'monthly', legal_name: '',
  });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/featured-artists/portal/wallet`, { params: { access_code: accessCode } });
      setWallet(data.wallet);
      setTransactions(data.transactions || []);
      if (data.wallet) {
        setForm({
          payout_method: data.wallet.payout_method || 'paypal',
          payout_handle: data.wallet.payout_handle || '',
          payout_name: data.wallet.payout_name || '',
          cadence: data.wallet.cadence || 'monthly',
          legal_name: data.wallet.legal_name || '',
        });
        if (!data.wallet.payout_method) setEditing(true);
      }
    } catch { /* silent */ }
    setLoading(false);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);
  if (loading && !wallet) {
    return null;
  }

  const save = async () => {
    if (!form.payout_handle.trim()) { toast.error('Payout handle required'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/featured-artists/portal/wallet/info`, { access_code: accessCode, ...form });
      toast.success('Payout info saved');
      setEditing(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const balance = wallet?.balance || 0;

  return (
    <div className="bg-[#141414] border border-[#DAA520]/30 p-5 space-y-4" data-testid="portal-payout-section">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h3 className="font-cabinet text-xl font-black uppercase tracking-tighter">SIDE WALLET</h3>
        <div className="bg-[#0A0A0A] border border-[#DAA520]/30 px-4 py-2">
          <p className="text-[9px] text-[#A3A3A3] uppercase tracking-wider">Available Balance</p>
          <p className="font-mono text-2xl font-bold text-[#DAA520]">${balance.toFixed(2)}</p>
        </div>
      </div>

      {!editing && wallet?.payout_method ? (
        <div className="space-y-2 text-sm">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <p className="text-[10px] text-[#A3A3A3] uppercase">Method</p>
              <p className="font-bold uppercase text-[#DAA520]">{wallet.payout_method}</p>
            </div>
            <div>
              <p className="text-[10px] text-[#A3A3A3] uppercase">Cadence</p>
              <p className="font-bold uppercase">{wallet.cadence}</p>
            </div>
          </div>
          <div>
            <p className="text-[10px] text-[#A3A3A3] uppercase">Handle / Account</p>
            <p className="font-mono">{wallet.payout_handle}</p>
          </div>
          <button onClick={() => setEditing(true)} className="text-[10px] uppercase tracking-wider px-3 py-1.5 border border-[#262626] hover:border-[#DAA520] hover:text-[#DAA520]" data-testid="portal-edit-payout">EDIT</button>
        </div>
      ) : (
        <div className="space-y-3" data-testid="portal-payout-form">
          <p className="text-xs text-[#A3A3A3]">Choose how you want to get paid. Admin sends payouts on your chosen cadence.</p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] uppercase">Method</Label>
              <select value={form.payout_method} onChange={(e) => setForm({ ...form, payout_method: e.target.value })}
                className="mt-1 w-full bg-[#0A0A0A] border border-[#262626] p-2 text-white text-sm" data-testid="portal-method-select">
                <option value="paypal">PayPal</option>
                <option value="zelle">Zelle</option>
                <option value="cashapp">CashApp</option>
                <option value="venmo">Venmo</option>
                <option value="bank">Bank (ACH)</option>
                <option value="stripe_connect">Stripe Connect (coming soon)</option>
                <option value="manual">Other / Manual</option>
              </select>
            </div>
            <div>
              <Label className="text-[10px] uppercase">Cadence</Label>
              <select value={form.cadence} onChange={(e) => setForm({ ...form, cadence: e.target.value })}
                className="mt-1 w-full bg-[#0A0A0A] border border-[#262626] p-2 text-white text-sm" data-testid="portal-cadence-select">
                <option value="weekly">Weekly</option>
                <option value="biweekly">Biweekly</option>
                <option value="monthly">Monthly</option>
                <option value="on_request">On request</option>
              </select>
            </div>
          </div>
          <div>
            <Label className="text-[10px] uppercase">Payout Handle / Account</Label>
            <Input value={form.payout_handle} onChange={(e) => setForm({ ...form, payout_handle: e.target.value })}
              placeholder="e.g. you@email.com (PayPal), $cashtag, phone for Zelle" className="bg-[#0A0A0A] border-[#262626] rounded-none text-sm" data-testid="portal-handle-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] uppercase">Recipient Name</Label>
              <Input value={form.payout_name} onChange={(e) => setForm({ ...form, payout_name: e.target.value })}
                placeholder="Name on account" className="bg-[#0A0A0A] border-[#262626] rounded-none text-sm" data-testid="portal-name-input" />
            </div>
            <div>
              <Label className="text-[10px] uppercase">Legal Name (for 1099)</Label>
              <Input value={form.legal_name} onChange={(e) => setForm({ ...form, legal_name: e.target.value })}
                placeholder="Optional" className="bg-[#0A0A0A] border-[#262626] rounded-none text-sm" data-testid="portal-legal-input" />
            </div>
          </div>
          <button onClick={save} disabled={saving}
            className="w-full px-4 py-2 bg-[#DAA520] hover:bg-white hover:text-[#0A0A0A] text-black font-black uppercase tracking-wider disabled:opacity-50 flex items-center justify-center gap-2"
            data-testid="portal-save-payout">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'SAVE PAYOUT INFO'}
          </button>
        </div>
      )}

      {transactions.length > 0 && (
        <details className="border-t border-[#262626] pt-3">
          <summary className="text-[10px] uppercase tracking-wider text-[#A3A3A3] cursor-pointer">Recent transactions ({transactions.length})</summary>
          <ul className="space-y-1 mt-2 text-[11px] font-mono">
            {transactions.slice(0, 10).map(t => (
              <li key={t.id} className="flex justify-between border-b border-[#1A1A1A] py-1">
                <span className="text-[#A3A3A3]">{new Date(t.created_at).toLocaleDateString()} · {t.type}</span>
                <span className={t.type === 'credit' ? 'text-[#22C55E]' : 'text-[#FF3B30]'}>${t.amount > 0 ? '+' : ''}{t.amount.toFixed(2)}</span>
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

